<template>
  <Transition name="cookie-fade">
    <div v-if="visible" class="cookie-consent-overlay">
    <div class="cookie-consent-banner">
      <div class="cookie-content">
        <div class="cookie-icon">🍪</div>
        <div class="cookie-text">
          <h3>{{ $t('cookie.title') }}</h3>
          <p>
            {{ $t('cookie.description') }}
          </p>
          <div class="cookie-links">
            <a href="/datenschutz" target="_blank" rel="noopener noreferrer" class="cookie-link">{{ $t('cookie.privacyLink') }}</a>
            <span class="cookie-divider">•</span>
            <a href="/impressum" target="_blank" rel="noopener noreferrer" class="cookie-link">{{ $t('cookie.imprintLink') }}</a>
          </div>
        </div>
      </div>
      <div class="cookie-actions">
        <button class="btn-accept-all" @click="acceptAll">
          {{ $t('cookie.acceptAll') }}
        </button>
        <button class="btn-essential" @click="acceptEssential">
          {{ $t('cookie.essentialOnly') }}
        </button>
        <button class="btn-settings" @click="showSettings = !showSettings">
          {{ $t('cookie.settings') }}
        </button>
      </div>
      
      <!-- Erweiterte Einstellungen -->
      <div v-if="showSettings" class="cookie-settings">
        <div class="setting-item">
          <div class="setting-info">
            <strong>{{ $t('cookie.essentialTitle') }}</strong>
            <p>{{ $t('cookie.essentialDesc') }}</p>
          </div>
          <div class="setting-toggle">
            <input type="checkbox" id="essential" checked disabled>
            <label for="essential">{{ $t('cookie.essentialLabel') }}</label>
          </div>
        </div>
        <div class="setting-item">
          <div class="setting-info">
            <strong>{{ $t('cookie.analyticsTitle') }}</strong>
            <p>{{ $t('cookie.analyticsDesc') }}</p>
          </div>
          <div class="setting-toggle">
            <input type="checkbox" id="analytics" v-model="analyticsEnabled">
            <label for="analytics">{{ $t('cookie.analyticsLabel') }}</label>
          </div>
        </div>
        <div class="setting-item">
          <div class="setting-info">
            <strong>{{ $t('cookie.communicationTitle') }}</strong>
            <p>{{ $t('cookie.communicationDesc') }}</p>
          </div>
          <div class="setting-toggle">
            <input type="checkbox" id="communication" v-model="communicationEnabled">
            <label for="communication">{{ $t('cookie.communicationLabel') }}</label>
          </div>
        </div>
      </div>
    </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import {
  CONSENT_VERSION,
  enableCommunication,
  initAnalyticsIfConsented,
  initCommunicationIfConsented,
  isConsentCurrent,
  loadAnalyticsScript
} from '../utils/analyticsConsent.js';

const visible = ref(false);
const showSettings = ref(false);
const analyticsEnabled = ref(false);
const communicationEnabled = ref(false);

onMounted(() => {
  initAnalyticsIfConsented();
  initCommunicationIfConsented();

  const consent = localStorage.getItem('cookie_consent');
  if (!consent || !isConsentCurrent()) {
    visible.value = true;
    return;
  }

  try {
    const parsed = JSON.parse(consent);
    analyticsEnabled.value = parsed.analytics || false;
    communicationEnabled.value = parsed.communication || false;
  } catch (e) {
    visible.value = true;
  }

  window.addEventListener('open-cookie-settings', openCookieSettings);
});

onUnmounted(() => {
  window.removeEventListener('open-cookie-settings', openCookieSettings);
});

function openCookieSettings() {
  const consent = localStorage.getItem('cookie_consent');
  if (consent) {
    try {
      const parsed = JSON.parse(consent);
      analyticsEnabled.value = parsed.analytics || false;
      communicationEnabled.value = parsed.communication || false;
    } catch (e) {
      analyticsEnabled.value = false;
      communicationEnabled.value = false;
    }
  }

  showSettings.value = true;
  visible.value = true;
}

// Emit visibility changes
watch(visible, (newValue) => {
  window.dispatchEvent(new CustomEvent('cookie-consent-visibility', { 
    detail: { visible: newValue } 
  }));
});

function acceptAll() {
  const consent = {
    essential: true,
    analytics: true,
    communication: true,
    date: new Date().toISOString(),
    version: CONSENT_VERSION
  };
  localStorage.setItem('cookie_consent', JSON.stringify(consent));
  visible.value = false;
  showSettings.value = false;
  
  enableAnalytics();
  enableCommunication();
  firePulsePixelEvent({
    action: 'cookieBannerAccpted'
  });
}

function acceptEssential() {
  const consent = {
    essential: true,
    analytics: false,
    communication: false,
    date: new Date().toISOString(),
    version: CONSENT_VERSION
  };
  localStorage.setItem('cookie_consent', JSON.stringify(consent));
  visible.value = false;
  showSettings.value = false;
}

function saveSettings() {
  const consent = {
    essential: true,
    analytics: analyticsEnabled.value,
    communication: communicationEnabled.value,
    date: new Date().toISOString(),
    version: CONSENT_VERSION
  };
  localStorage.setItem('cookie_consent', JSON.stringify(consent));
  visible.value = false;
  showSettings.value = false;
  
  if (analyticsEnabled.value) {
    enableAnalytics();
  }
  if (communicationEnabled.value) {
    enableCommunication();
  }
}

function enableAnalytics() {
  loadAnalyticsScript();
  window.dispatchEvent(new CustomEvent('analytics-consent-granted'));
}

function firePulsePixelEvent(payload, attempt = 0) {
  const spPin = window.SP_PIN;
  if (spPin && typeof spPin.fireEvent === 'function') {
    spPin.fireEvent('077437c1-6270-4c88-9cb9-1096d8f75094', payload);
    return;
  }

  if (attempt < 10) {
    setTimeout(() => {
      firePulsePixelEvent(payload, attempt + 1);
    }, 300);
  }
}

// Save settings when optional toggles change
watch(analyticsEnabled, () => {
  if (showSettings.value) {
    saveSettings();
  }
});

watch(communicationEnabled, () => {
  if (showSettings.value) {
    saveSettings();
  }
});
</script>

<style scoped>
.cookie-consent-overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: linear-gradient(to top, var(--overlay-dark) 0%, transparent 100%);
  padding: 20px;
  backdrop-filter: blur(8px);
  pointer-events: auto;
}

.cookie-consent-banner {
  max-width: 1200px;
  margin: 0 auto;
  background: var(--glass-bg);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(12px);
  pointer-events: auto;
}

.cookie-content {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}

.cookie-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.cookie-text h3 {
  color: var(--text-primary);
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.cookie-text p {
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0 0 12px 0;
}

.cookie-links {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.cookie-link {
  color: var(--accent-green, #00ffb3);
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 500;
  transition: color 0.2s ease;
}

.cookie-link:hover {
  color: var(--text-primary);
  text-decoration: underline;
}

.cookie-divider {
  color: var(--text-muted, #64748b);
  font-size: 0.8rem;
}

.cookie-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.btn-accept-all {
  background: var(--gradient-green-blue);
  color: var(--text-primary);
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 255, 179, 0.3);
}

.btn-accept-all:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 255, 179, 0.4);
}

.btn-essential {
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-essential:hover {
  border-color: var(--text-muted, #64748b);
  color: var(--text-primary);
  transform: translateY(-2px);
}

.btn-settings {
  background: transparent;
  color: #94a3b8;
  border: none;
  padding: 12px 16px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: underline;
}

.btn-settings:hover {
  color: var(--text-primary);
}

.cookie-settings {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.setting-info {
  flex: 1;
}

.setting-info strong {
  color: var(--text-primary);
  font-size: 0.9rem;
  display: block;
  margin-bottom: 4px;
}

.setting-info p {
  color: #94a3b8;
  font-size: 0.8rem;
  margin: 0;
  line-height: 1.4;
}

.setting-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.setting-toggle input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #00ffb3;
}

.setting-toggle label {
  color: #cbd5e1;
  font-size: 0.8rem;
  cursor: pointer;
}

.setting-toggle input:disabled + label {
  color: #64748b;
  cursor: not-allowed;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .cookie-consent-overlay {
    padding: 16px;
  }
  
  .cookie-consent-banner {
    padding: 20px;
  }
  
  .cookie-content {
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
  }
  
  .cookie-icon {
    font-size: 1.5rem;
  }
  
  .cookie-text h3 {
    font-size: 1.1rem;
  }
  
  .cookie-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .btn-accept-all,
  .btn-essential {
    width: 100%;
    text-align: center;
  }
  
  .setting-item {
    flex-direction: column;
    gap: 8px;
  }
  
  .setting-toggle {
    justify-content: flex-start;
  }
}

@media (max-width: 480px) {
  .cookie-links {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .cookie-divider {
    display: none;
  }
}

/* Fade Transition */
.cookie-fade-enter-active,
.cookie-fade-leave-active {
  transition: all 0.3s ease;
}

.cookie-fade-enter-from,
.cookie-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.cookie-fade-enter-to,
.cookie-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
